
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Validate } from '@/types';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import CustomFields from './CustomFields.vue';
import CustomQuestions from './CustomQuestions.vue';

const namespace: string = 'orders';

@Component<SpecialRequests>({
	components: {
		InfoBanner,
		CustomFields,
		CustomQuestions,
		ValidationObserver,
		ValidationProvider
	}
})
export default class SpecialRequests extends Vue {
	@Action('setNotes', { namespace }) private setNotes!: (notes: string) => void;
	@Getter('getNotes', { namespace }) private notes!: string;
	@Getter('getOrderDetails', { namespace }) private orderDetails!: OrderDetails;
	@Getter('getRepeatOrderNotices', { namespace }) private repeatOrderNotices!: RepeatOrderNotices | null;
	@Getter('getModifyingOrder', { namespace }) private isModifyingOrder!: boolean;
	@Getter('getCheckoutAnsweredQuestions', { namespace }) private checkoutAnsweredQuestions!: AnsweredCustomQuestion[] | null;
	@Getter('getCheckoutQuestions', { namespace: 'customQuestions' }) private questions!: CustomQuestion[];
	@Getter('hideTakeoutSpecialInstructions', { namespace: 'auth' }) private hideTakeoutSpecialInstructions!: boolean;
	@Getter('areCateringEventsEnabled', { namespace: 'auth' }) private areCateringEventsEnabled!: boolean;
	@Prop({ type: Boolean, default: false }) private hideHeader!: boolean;

	$refs!: { observer: Validate; };

	private specialInstructions: string = '';

	/**
	 * Default questions to set in the custom fields, excludes questions the operator has removed
	 * No defaults when modifying or repeating an order, so the order's custom questions stay untouched
	 *
	 * @return {CustomQuestion[]}
	 */
	private get defaultQuestions(): CustomQuestion[] {
		if (this.isModifyingOrder || this.repeatOrderNotices) {
			return [];
		}
		return this.questions;
	}

	/**
	 * Show the pre-filled notice when repeating an order, only if this form holds values carried over from it.
	 * Catering events show their questions in a separate custom fields section, which has its own notice.
	 *
	 * @return {boolean}
	 */
	private get showPrefilledNotice(): boolean {
		if (!this.repeatOrderNotices) {
			return false;
		}
		const hasPrefilledQuestions: boolean = this.orderDetails.takeoutType !== 'catering-event' && !!this.checkoutAnsweredQuestions?.length;
		const hasPrefilledNotes: boolean = !this.hideTakeoutSpecialInstructions && !!this.notes;
		return hasPrefilledNotes || hasPrefilledQuestions;
	}

	/**
	 * @return {void}
	 */
	private created(): void {
		if (this.notes) {
			this.specialInstructions = this.notes;
		}
	}
}
